import React, { Fragment, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';

import EditIcon from '@mui/icons-material/EditOutlined';
import MoreIcon from '@mui/icons-material/MoreVert';
import DisableIcon from '@mui/icons-material/NotInterested';
import PersonPinCircleIcon from '@mui/icons-material/PersonPinCircle';

import Table from '../shared/table';

import './companies.scss';

const getCompanyLogoUrl = (logo) => (
  typeof logo === 'string' ? logo : logo?.downloadUrl || ''
);

const CompaniesTable = ({
  companies = [],
  onEditClick = () => {},
  onDisableClick = () => {},
  onManageCountriesClick = () => {},
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  totalRows,
  order,
  setOrder,
  orderBy,
  setOrderBy,
}) => {

  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const columns = useMemo(() => {
    return [
      {
        title: 'Name',
        field: 'name',
        type: 'custom',
        sort: true,
        formatter: (value, row) => (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {!!getCompanyLogoUrl(row?.logo) && (
              <Box
                component="img"
                src={getCompanyLogoUrl(row.logo)}
                alt={`${value}-company-logo`}
                sx={{
                  width: 30,
                  height: 30,
                  minWidth: 30,
                  objectFit: 'contain',
                  borderRadius: '8px',
                  border: '1px solid var(--color-border)',
                  backgroundColor: 'var(--color-surface)',
                  p: '2px',
                  mr: 1,
                }}
              />
            )}
            <span style={{ fontWeight: 'bold' }}>{value}</span>
          </Box>
        )
      },
      {
        title: 'Countries',
        field: 'countries',
        type: 'custom',
        sort: true,
        formatter: (value) => (
          value?.map((country) => (
            <Chip
              key={country}
              size="small"
              label={country}
              variant="outlined"
              sx={{ fontWeight: 500, backgroundColor: 'var(--color-main-light)', borderColor: 'var(--color-main-border)', mr: 1 }}
            />
          ))
        ),
      },
      { title: 'Address', field: 'address', type: 'default', sort: true },
      { title: 'State', field: 'state', type: 'default', sort: true },
      { title: 'Post Code', field: 'postCode', type: 'default', sort: true },
      {
        title: 'Contact Info',
        field: 'email',
        type: 'custom',
        formatter: (value, row) => (
          <Box>
            <Box sx={{ fontWeight: 'bold' }}>{row.email}</Box>
            <Box sx={{ mt: '2px' }}>{row.phone}</Box>
          </Box>
        )
      },
      { title: 'Country HQ', field: 'country', type: 'default', sort: true },
      {
        title: 'Color',
        field: 'color',
        type: 'custom',
        formatter: (value) => (
          <Box sx={{ height: 30, width: 30, padding: '3px', borderRadius: '6px', border: '1px solid #eeeeee' }}>
            <Box sx={{ height: '100%', width: '100%', backgroundColor: value, borderRadius: '4px' }} />
          </Box>
        )
      },
      {
        title: '',
        field: 'id',
        type: 'custom',
        filter: false,
        width: 50,
        formatter: (value, row) => (
          <>
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              aria-controls={`${row.id}_actions`}
              aria-haspopup="true"
              aria-label="actions"
              color="inherit"
              id={`${row.id}_actions`}
            >
              <MoreIcon color="secondary" />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              elevation={1}
              id={`${row.id}_actions`}
              keepMounted
              onClose={handleMenuClose}
              open={Boolean(anchorEl) && anchorEl.id === `${row.id}_actions`}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem
                onClick={() => {
                  onEditClick(row);
                  handleMenuClose();
                }}
              >
                <EditIcon fontSize="small" style={{ marginRight: 8 }} color="secondary" />
                Edit Company
              </MenuItem>
              <MenuItem
                onClick={() => {
                  onManageCountriesClick(row);
                  handleMenuClose();
                }}
              >
                <PersonPinCircleIcon fontSize="small" style={{ marginRight: 8 }} color="secondary" />
                Manage Countries
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={() => {
                  onDisableClick(row?.id);
                  handleMenuClose();
                }}
              >
                <DisableIcon fontSize="small" style={{ marginRight: 8, fill: '#F04438' }} />
                <Typography sx={{ color: '#F04438' }}>Disable Company</Typography>
              </MenuItem>
            </Menu>
          </>
        ),
      },
    ];
  }, [anchorEl, onDisableClick, onEditClick, onManageCountriesClick]);

  return (
    <Fragment>
      <Table
        stickyTable
        rows={companies?.map((company) => ({
          ...company,
          country: company?.country || '',
        }))}
        columns={columns}
        page={page}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        totalRows={totalRows}
        order={order}
        setOrder={setOrder}
        orderBy={orderBy}
        setOrderBy={setOrderBy}
      />
    </Fragment>
  );
};

CompaniesTable.propTypes = {
  companies: PropTypes.array,
  distributors: PropTypes.array,
  onVerifyClick: PropTypes.func,
  onSMSClick: PropTypes.func
};

export default CompaniesTable;
